import { CREAM, GRAD, INK, LINE, ORANGE, PAPER, SHADOW_CARD, SHADOW_GLOW, SHADOW_GLOW_STRONG, CORAL_DEEP } from './theme.ts';
import { Icon } from './Icon.tsx';

export interface OptionRowProps {
  label: string;
  emoji?: string;
  icon?: string;
  selected: boolean;
  /** multi-select rows carry a 24px check circle */
  checkbox?: boolean;
  role?: 'radio' | 'checkbox';
  onSelect: () => void;
}

/** 64px row, radius 20, 34px cream glyph badge; selected = orange border + cream fill. */
export function OptionRow({ label, emoji, icon, selected, checkbox, role = 'radio', onSelect }: OptionRowProps) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={selected}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 64,
        padding: '12px 14px',
        borderRadius: 20,
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: INK,
        background: selected ? CREAM : PAPER,
        border: `2px solid ${selected ? ORANGE : LINE}`,
        boxShadow: SHADOW_CARD,
        transition: 'all .15s',
      }}
    >
      {checkbox ? (
        <span
          style={{
            width: 24,
            height: 24,
            flex: 'none',
            borderRadius: '50%',
            border: `2px solid ${selected ? 'transparent' : LINE}`,
            background: selected ? GRAD : PAPER,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <i className="ph-bold ph-check" style={{ fontSize: 14, opacity: selected ? 1 : 0 }} aria-hidden="true" />
        </span>
      ) : null}
      <span
        style={{
          width: 34,
          height: 34,
          flex: 'none',
          borderRadius: 11,
          background: CREAM,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
        }}
      >
        {icon ? <Icon name={icon} size={22} color={CORAL_DEEP} /> : emoji}
      </span>
      <span style={{ fontWeight: 600, flex: 1 }}>{label}</span>
    </button>
  );
}

/** two-up tile: first tile gradient primary + glow, second cream. */
export function OptionTile({
  label,
  icon,
  selected,
  primary,
  onSelect,
}: {
  label: string;
  icon?: string;
  selected: boolean;
  primary: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className="fk-tile"
      style={{
        width: '100%',
        aspectRatio: '1',
        borderRadius: 28,
        cursor: 'pointer',
        font: 'inherit',
        color: primary ? '#fff' : INK,
        background: primary ? GRAD : CREAM,
        border: 0,
        boxShadow: selected ? SHADOW_GLOW_STRONG : primary ? SHADOW_GLOW : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 18,
        transition: 'transform .15s, box-shadow .15s',
        outline: selected ? `3px solid ${ORANGE}` : undefined,
        outlineOffset: selected ? 2 : undefined,
      }}
    >
      <span
        style={{
          width: 64,
          height: 64,
          borderRadius: 22,
          background: primary ? 'rgba(255,255,255,.25)' : PAPER,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: primary ? '#fff' : CORAL_DEEP,
        }}
      >
        <Icon name={icon} size={34} />
      </span>
      <span style={{ fontSize: 17, fontWeight: 800, textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
    </button>
  );
}
